import { useEffect, useMemo, useState } from 'react'
import type { CarteDTO, LieuDTO, RegionDTO } from '@whatremains/shared'
import { useAuDoigt } from './pointeur'

/**
 * La carte, dessinée.
 *
 * Vectorielle et tracée à la main, comme les fiches d'artefact : c'est le même
 * geste et les mêmes tokens. Rien n'est peint, rien n'est importé, rien ne pèse.
 *
 * Ce qu'elle doit dire d'un coup d'œil, `08-INTERFACE.md` §5 :
 *   où suis-je · où puis-je aller et à quel prix · qu'y a-t-il là-bas.
 *
 * Un lieu non découvert **existe sans être nommé** : on le voit, on ne sait pas
 * ce que c'est. Le serveur ne l'envoie pas autrement.
 */

/**
 * Le trait de côte du Relevé.
 *
 * Dessiné à la main autour des sept régions. Il ne dit rien du jeu : il donne
 * une forme à un monde dont toutes les distances sont mesurées depuis une seule
 * pierre, au milieu.
 */
const COTE =
  'M 100 610 C 45 560, 25 470, 48 380 C 66 305, 45 215, 108 168 ' +
  'C 170 122, 255 155, 330 122 C 400 92, 452 108, 520 72 ' +
  'C 592 34, 690 24, 776 42 C 862 60, 946 96, 962 180 ' +
  'C 978 268, 932 336, 940 408 C 948 486, 984 548, 934 606 ' +
  'C 884 664, 790 636, 700 656 C 610 676, 546 656, 456 674 ' +
  'C 366 692, 292 700, 212 682 C 154 668, 126 650, 100 610 Z'

/** Deux cours d'eau. Ils servent à orienter l'œil, pas à jouer. */
const EAUX = [
  'M 300 622 C 322 568, 312 520, 348 482 C 384 444, 442 448, 482 420 C 520 394, 534 348, 570 320',
  'M 52 402 C 108 394, 144 420, 192 424 C 246 428, 274 460, 310 468',
]

/**
 * Le relief des Hauts Relevés et de La Ligne, en hachures courtes.
 * Aucune information de jeu : c'est de la texture, posée sous les étiquettes.
 */
const RELIEF: [number, number][] = [
  [524, 224], [550, 212], [576, 222], [602, 208], [628, 220], [654, 210], [680, 222],
  [560, 248], [588, 240], [616, 252], [644, 242],
  [772, 198], [798, 188], [824, 196], [850, 186], [806, 216], [834, 208],
]

function Glyphe({ l, taille }: { l: LieuDTO; taille: number }) {
  const t = taille
  switch (l.type) {
    case 'capitale':
      return (
        <>
          <rect x={-t} y={-t} width={t * 2} height={t * 2} />
          <rect x={-t * 0.42} y={-t * 0.42} width={t * 0.84} height={t * 0.84} fill="var(--bg)" />
        </>
      )
    case 'ville':
      return <circle r={t} />
    case 'relais':
      return (
        <>
          <circle r={t * 0.75} />
          <rect x={-t * 1.5} y={-0.6} width={t * 3} height={1.2} />
        </>
      )
    case 'port':
      return (
        <>
          <circle r={t * 0.7} />
          <rect x={-t * 1.4} y={t * 0.9} width={t * 2.8} height={1.2} />
        </>
      )
    case 'ruine':
      return <path d={`M ${-t} ${t} L ${-t * 0.3} ${-t} L ${t * 0.2} ${t * 0.2} L ${t} ${-t * 0.6}`} fill="none" strokeWidth={1.6} />
    case 'donjon':
      return <path d={`M ${-t} ${-t} L ${t} ${-t} L 0 ${t * 1.2} Z`} />
    default:
      return <path d={`M 0 ${-t} L ${t} 0 L 0 ${t} L ${-t} 0 Z`} />
  }
}

function tailleDe(l: LieuDTO): number {
  if (l.type === 'capitale') return 8
  if (l.type === 'ville') return 6
  return 5
}

/* ── Le placement des étiquettes ─────────────────────────────────────────────
 *
 * Trente-sept noms sur une carte de mille points de large : posés bêtement sous
 * leur glyphe, six se chevauchent, et deux passent sous le titre de leur région.
 * Une carte dont on ne peut pas lire les noms ne sert à rien.
 *
 * On résout en glouton, sans bibliothèque : chaque nom essaie quatre positions
 * autour de son point, dans l'ordre de préférence, et prend la première qui ne
 * touche ni un nom déjà posé, ni un titre de région, ni un glyphe. Les lieux
 * importants passent en premier et obtiennent donc la meilleure place.
 *
 * Un nom qui ne trouve aucune place libre n'est **pas** affiché en se
 * superposant : il est masqué, et reste lisible au survol comme tous les
 * autres. Mieux vaut un nom absent qu'une bouillie de deux noms.
 */

type Boite = { x1: number; y1: number; x2: number; y2: number }

/** Largeur approchée d'un texte, en unités du viewBox. Suffisant pour éviter. */
function largeur(texte: string, taillePolice: number): number {
  return texte.length * taillePolice * 0.52
}

function seTouchent(a: Boite, b: Boite): boolean {
  return a.x1 < b.x2 && b.x1 < a.x2 && a.y1 < b.y2 && b.y1 < a.y2
}

export type Etiquette = { dx: number; dy: number; ancre: 'middle' | 'start' | 'end' }

const TAILLE_NOM = 10.5

/**
 * Les quatre poses candidates, dans l'ordre de préférence : sous le glyphe,
 * dessus, à droite, à gauche. `dy` vise la ligne de base du texte.
 */
function posesDe(t: number): Etiquette[] {
  return [
    { dx: 0, dy: t + 12, ancre: 'middle' },
    { dx: 0, dy: -t - 6, ancre: 'middle' },
    { dx: t + 5, dy: 4, ancre: 'start' },
    { dx: -t - 5, dy: 4, ancre: 'end' },
  ]
}

/** Le rectangle réellement occupé par un nom posé selon `e`. Un seul endroit. */
function boiteDe(l: LieuDTO, e: Etiquette): Boite {
  const w = largeur(l.nom, TAILLE_NOM)
  const x = l.x + e.dx
  const gauche = e.ancre === 'middle' ? x - w / 2 : e.ancre === 'start' ? x : x - w
  const base = l.y + e.dy
  return { x1: gauche - 2, y1: base - TAILLE_NOM + 1, x2: gauche + w + 2, y2: base + 3 }
}

const PRIORITE: Record<string, number> = { capitale: 0, ville: 1, port: 2, donjon: 3, relais: 4 }

function placerEtiquettes(regions: RegionDTO[]): Map<string, Etiquette> {
  const occupe: Boite[] = []
  const place = new Map<string, Etiquette>()

  // Les titres de région sont posés d'avance : ils ne cèdent à personne.
  //
  // Ils sont AU-DESSUS du cercle, pas dedans. Placés à l'intérieur, un lieu que
  // la spirale envoyait vers le haut tombait dessous, et le titre n'a pas de
  // pose de repli : c'est le lieu qui aurait dû bouger, ce qu'on ne veut pas.
  //
  // Ils portent 0.22em d'interlettrage dans la CSS, d'où le facteur : sans lui
  // la boîte fait deux tiers de la largeur réelle et le titre déborde.
  for (const r of regions) {
    const w = largeur(r.nom, 9) * 1.42
    occupe.push({ x1: r.x - w / 2 - 3, y1: r.y - r.rayon - 29, x2: r.x + w / 2 + 3, y2: r.y - r.rayon - 4 })
  }

  // Les glyphes aussi : un nom ne doit jamais recouvrir un point cliquable.
  const lieux = regions.flatMap((r) => r.lieux)
  for (const l of lieux) {
    const t = tailleDe(l) + 3
    occupe.push({ x1: l.x - t, y1: l.y - t, x2: l.x + t, y2: l.y + t })
  }

  // Les plus gros lieux choisissent en premier, donc obtiennent la meilleure pose.
  const ordre = [...lieux].sort(
    (a, b) => (PRIORITE[a.type] ?? 5) - (PRIORITE[b.type] ?? 5) || a.code.localeCompare(b.code),
  )

  for (const l of ordre) {
    if (!l.decouvert) continue
    for (const pose of posesDe(tailleDe(l))) {
      const boite = boiteDe(l, pose)
      if (occupe.some((o) => seTouchent(boite, o))) continue
      occupe.push(boite)
      place.set(l.code, pose)
      break
    }
  }

  return place
}

export function CarteDessinee({
  carte,
  surLieu,
}: {
  carte: CarteDTO
  surLieu: (l: LieuDTO) => void
}) {
  // Deux survols distincts : une région, ou un lieu. Le lieu l'emporte quand les
  // deux sont vrais, parce qu'il est au-dessus et plus précis.
  const [survole, setSurvole] = useState<{ l: LieuDTO; r: RegionDTO } | null>(null)
  const [zoneSurvolee, setZoneSurvolee] = useState<RegionDTO | null>(null)
  const auDoigt = useAuDoigt()

  /**
   * Sur quoi la carte est cadrée : le monde, ou une région.
   *
   * La carte du monde entier tient sur un grand écran et pas sur un téléphone,
   * où elle débordait et défilait. Défiler pour voir le sud, c'est perdre d'un
   * côté ce qu'on gagne de l'autre, et `08-INTERFACE.md` §5 demande qu'elle se
   * lise d'un coup d'œil.
   *
   * Le cadrage par région règle les deux : au doigt on part de là où l'on est,
   * à la souris on part du monde, et un clic passe de l'un à l'autre dans les
   * deux sens. Rien n'est retiré, c'est la focale qui change.
   */
  const [cadre, setCadre] = useState<string | null>(null)
  const [cadreInitial, setCadreInitial] = useState(false)

  const regions = useMemo(() => carte.continents.flatMap((c) => c.regions), [carte])

  // Les routes entre régions voisines, une seule fois par paire.
  const routes = useMemo(() => {
    const parCode = new Map(regions.map((r) => [r.code, r]))
    const paires: { a: RegionDTO; b: RegionDTO }[] = []
    for (const r of regions) {
      for (const v of r.voisines) {
        const autre = parCode.get(v)
        if (autre && r.code < autre.code) paires.push({ a: r, b: autre })
      }
    }
    return paires
  }, [regions])

  const etiquettes = useMemo(() => placerEtiquettes(regions), [regions])

  // Au doigt, on ouvre sur sa propre région. Une fois seulement : si le joueur
  // décide ensuite de regarder le monde entier, on ne le lui reprend pas.
  useEffect(() => {
    if (cadreInitial || !auDoigt) return
    setCadreInitial(true)
    const ici = regions.find((r) => r.ici)
    if (ici) setCadre(ici.code)
  }, [auDoigt, cadreInitial, regions])

  /*
   * Le cadre du monde se CALCULE, il n'est plus écrit.
   *
   * Il valait `0 0 1000 700`, ce qui marche tant que le monde tient dedans —
   * il y tient aujourd'hui, de 83 à 870 en x et de 126 à 612 en y. Mais rien
   * ne l'imposait : une région écrite plus loin serait sortie du cadre et
   * n'aurait simplement pas existé à l'écran, sans qu'aucun test ni aucune
   * erreur ne le signale.
   *
   * Enzo : « il faut que la carte puisse rester lisible et utilisable à court,
   * mais aussi à moyen et long terme. » Un cadre déduit des données est la
   * première condition : le monde peut grandir dans n'importe quelle direction
   * sans qu'on ait à y penser.
   */
  const mondeVue = useMemo(() => {
    if (regions.length === 0) return '0 0 1000 700'
    const xs = regions.flatMap((r) => [r.x - r.rayon, r.x + r.rayon])
    const ys = regions.flatMap((r) => [r.y - r.rayon, r.y + r.rayon])
    const x0 = Math.min(...xs), x1 = Math.max(...xs)
    const y0 = Math.min(...ys), y1 = Math.max(...ys)
    // Une marge d'un vingtième : les noms de région débordent de leur cercle,
    // et un monde collé aux bords se lit mal.
    const marge = Math.max(x1 - x0, y1 - y0) * 0.05
    return `${x0 - marge} ${y0 - marge} ${x1 - x0 + marge * 2} ${y1 - y0 + marge * 2}`
  }, [regions])

  /*
   * Les lieux ne se dessinent au monde que tant qu'ils y tiennent.
   *
   * Trente-sept aujourd'hui, et la vue monde les porte très bien. Le plafond
   * passé à 200 et les régions à venir en promettent beaucoup plus, et il n'y a
   * pas de seuil naturel : à cent cinquante lieux dans le même cadre, les noms
   * se recouvrent et la carte cesse de servir à ce pour quoi on l'ouvre.
   *
   * On ne fixe donc pas la limite au jugé sur un nombre de régions mais sur ce
   * qui se voit vraiment : la DENSITÉ. Au-delà, le monde ne montre plus que ses
   * régions — ce qui est de toute façon le bon niveau de détail pour décider où
   * aller — et les lieux réapparaissent dès qu'on cadre une région.
   */
  const lieuxAuMonde = useMemo(() => {
    const n = regions.reduce((t, r) => t + r.lieux.length, 0)
    return n <= 60
  }, [regions])

  const regionCadree = cadre ? regions.find((r) => r.code === cadre) : undefined
  const vue = regionCadree
    ? (() => {
        // Une marge d'un tiers du rayon : assez pour que les routes vers les
        // régions voisines sortent du cadre, ce qui montre qu'il y a un ailleurs.
        const m = regionCadree.rayon * 1.34
        return `${regionCadree.x - m} ${regionCadree.y - m} ${m * 2} ${m * 2}`
      })()
    : mondeVue

  return (
    <div className="carte-dessinee">
      {/*
        Le dessin et son bouton de retour partagent ce cadre, et c'est sa seule
        raison d'être : ancré au bloc entier, le bouton venait se poser sur le
        panneau de texte qui suit la carte.
      */}
      <div className="carte-dessin">
      <svg viewBox={vue} role="img" aria-label="Carte du Relevé">
        <defs>
          <pattern id="grainCarte" width="3" height="3" patternUnits="userSpaceOnUse">
            <rect width="3" height="3" fill="none" />
            <circle cx="0.5" cy="0.5" r="0.35" fill="rgba(244,233,204,.05)" />
          </pattern>
        </defs>

        <path d={COTE} className="terre" />
        <path d={COTE} className="terre-grain" />
        <path d={COTE} className="cote" />

        {EAUX.map((d, i) => (
          <path key={i} d={d} className="eau" />
        ))}

        {RELIEF.map(([x, y], i) => (
          <path key={i} d={`M ${x} ${y} l 7 -7 l 7 7`} className="relief" />
        ))}

        {routes.map(({ a, b }, i) => (
          <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} className="route" />
        ))}

        {regions.map((r) => (
          <g
            key={r.code}
            className={`zone${r.ici ? ' zone-ici' : ''}`}
            onMouseEnter={() => !auDoigt && setZoneSurvolee(r)}
            onMouseLeave={() => !auDoigt && setZoneSurvolee(null)}
            onClick={() => {
              // Au doigt, le premier appui montre la région dans l'infobulle,
              // le second la cadre. Même règle que partout ailleurs.
              if (auDoigt && zoneSurvolee?.code !== r.code) {
                setZoneSurvolee(r)
                return
              }
              setCadre(cadre === r.code ? null : r.code)
            }}
          >
            <circle cx={r.x} cy={r.y} r={r.rayon} />
            <text x={r.x} y={r.y - r.rayon - 19} textAnchor="middle" className="zone-nom">
              {r.nom.toUpperCase()}
            </text>
            <text x={r.x} y={r.y - r.rayon - 8} textAnchor="middle" className="zone-meta">
              niv {r.seuilNiveau}
              {r.reste.quetesDisponibles > 0 && ` · ${r.reste.quetesDisponibles} à prendre`}
            </text>
          </g>
        ))}

        {/*
          Cadré, on dessine TOUT, et c'est voulu : le SVG découpe déjà ce qui
          sort du cadre, et la marge d'un tiers de rayon existe précisément pour
          laisser paraître les lieux voisins au bord. Ne dessiner que la région
          cadrée ferait partir les routes vers du vide.
        */}
        {(lieuxAuMonde || regionCadree ? regions : []).flatMap((r) =>
          r.lieux.map((l) => {
            const t = tailleDe(l)
            const etiquette = etiquettes.get(l.code)
            const classes = [
              'lieu-carte',
              l.decouvert ? 'connu' : 'inconnu',
              l.ici ? 'ici' : '',
              survole?.l.code === l.code ? 'visee' : '',
              !l.accessible && !l.ici ? 'ferme' : '',
            ].join(' ')

            return (
              <g
                key={l.code}
                transform={`translate(${l.x} ${l.y})`}
                className={classes}
                onClick={() => {
                  // Au doigt, un appui montre le lieu et le suivant y voyage :
                  // sans ça on partirait sans avoir lu le prix du trajet.
                  if (auDoigt && survole?.l.code !== l.code) {
                    setSurvole({ l, r })
                    return
                  }
                  if (l.accessible || l.ici) surLieu(l)
                }}
                onMouseEnter={() => !auDoigt && setSurvole({ l, r })}
                onMouseLeave={() => !auDoigt && setSurvole(null)}
                tabIndex={l.accessible || l.ici || auDoigt ? 0 : -1}
                role="button"
                aria-label={l.decouvert ? l.nom : 'Lieu inconnu'}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && (l.accessible || l.ici)) {
                    e.preventDefault()
                    surLieu(l)
                  }
                }}
              >
                <circle r={t + 9} className="cible" />
                {l.ici && <circle r={t + 6} className="anneau-ici" />}
                <Glyphe l={l} taille={t} />

                {etiquette && (
                  <text
                    x={etiquette.dx}
                    y={etiquette.dy}
                    textAnchor={etiquette.ancre}
                    className="lieu-nom"
                  >
                    {l.nom}
                  </text>
                )}

                {/*
                  Le NOMBRE de quêtes, pas seulement qu'il y en a.

                  La pastille était un point : elle disait qu'il y avait
                  quelque chose, jamais combien. Enzo voulait le compte, et il a
                  raison — un lieu avec une quête et un lieu avec quatre ne se
                  visitent pas dans le même ordre.
                */}
                {(l.quetesEnCours > 0 || l.quetesDisponibles > 0) && (
                  <g transform={`translate(${t + 5} ${-t - 4})`}>
                    <circle
                      r={5}
                      className={l.quetesEnCours > 0 ? 'marque-cours' : 'marque-dispo'}
                    />
                    {/*
                      Le chiffre prend la couleur de ce qu'il y a DERRIÈRE lui.

                      Signalé par Enzo : « le chiffre est noir sur du noir, on ne
                      voit pas du tout le nombre de quêtes ». Il était peint en
                      couleur de fond dans les deux cas — juste sur la pastille
                      pleine des quêtes en cours, où le fond est jaune, et
                      invisible sur la pastille creuse des quêtes à prendre, où
                      il n'y a rien derrière que la carte.

                      Le défaut ne se voyait donc que sur la moitié des lieux, et
                      justement celle qui compte le plus : ceux qu'on n'a pas
                      encore visités.
                    */}
                    <text
                      className={`marque-compte ${l.quetesEnCours > 0 ? 'sur-plein' : 'sur-creux'}`}
                      textAnchor="middle"
                      dy="2.1"
                    >
                      {l.quetesEnCours > 0 ? l.quetesEnCours : l.quetesDisponibles}
                    </text>
                  </g>
                )}
              </g>
            )
          }),
        )}
      </svg>

      {/*
        Revenir au monde. Présent seulement quand on est cadré quelque part :
        un bouton qui ne fait rien quand on est déjà au monde entier serait du
        bruit, et l'écran en a déjà assez.
      */}
      {regionCadree && (
        <button className="carte-retour" onClick={() => setCadre(null)}>
          Voir tout le Relevé
        </button>
      )}
      </div>

      {/*
        Même mécanique que le panneau de la fiche, et pour le même défaut :
        la boîte réserve la place pour que la carte au-dessus ne bouge pas,
        le corps se peint par-dessus ce qui suit. Mesuré : le plus long texte
        de lieu déborde de 31 px même sur un écran de 1024, et de 53 px sur un
        téléphone. C'était donc une barre de défilement inatteignable ici
        aussi, simplement personne ne l'avait encore rencontrée.
      */}
      <div className="carte-infobulle">
        <div className="carte-infobulle-corps" aria-live="polite">
        {survole ? (
          <>
            <b>{survole.l.decouvert ? survole.l.nom : 'Tu ne sais pas ce qu’il y a là'}</b>
            <span>
              {survole.r.nom}
              {survole.l.ici
                ? ' · tu y es'
                : survole.l.empechement
                  ? ` · ${survole.l.empechement}`
                  : survole.l.coutVoyage > 0
                    ? ` · ${survole.l.coutVoyage} or`
                    : ' · à pied'}
            </span>
            {survole.l.decouvert && survole.l.texte && <i>{survole.l.texte}</i>}
          </>
        ) : zoneSurvolee ? (
          <>
            <b>{zoneSurvolee.nom}</b>
            <span>
              {`acte ${zoneSurvolee.acte} · niveau ${zoneSurvolee.seuilNiveau} · danger `}
              {'▮'.repeat(zoneSurvolee.danger)}
              {'▯'.repeat(5 - zoneSurvolee.danger)}
              {` · ${zoneSurvolee.controle ? zoneSurvolee.controle.nom : 'sans contrôle'}`}
              {zoneSurvolee.reste.quetesEnCours > 0 && ` · ${zoneSurvolee.reste.quetesEnCours} en cours`}
              {zoneSurvolee.reste.quetesDisponibles > 0 && ` · ${zoneSurvolee.reste.quetesDisponibles} à prendre`}
              {zoneSurvolee.reste.lieuxNonDecouverts > 0 &&
                ` · ${zoneSurvolee.reste.lieuxNonDecouverts} à découvrir`}
            </span>
            <i>{zoneSurvolee.texte}</i>
          </>
        ) : (
          <span className="tenu">
            {auDoigt ? 'Appuie sur une région ou un lieu.' : 'Survole une région ou un lieu.'}
          </span>
        )}
        </div>
      </div>
    </div>
  )
}
